import { useState, useEffect } from 'react';
import { useTheme } from '../theme';
import { db } from '../dbClient';
import { useToast } from '../Toast';

const BUILTIN_SOURCES = [
  {
    id: 'ofac',
    name: 'OFAC Sanctions List',
    type: 'government',
    category: 'sanctions',
    icon: '🏛️',
    description: 'US Treasury Office of Foreign Assets Control sanctions list',
    capabilities: ['Entity screening', 'Real-time updates', 'Full address matching'],
    status: 'active',
    region: 'Global',
    updateFrequency: 'Daily',
    documentation: 'https://home.treasury.gov/policy-issues/financial-sanctions-and-embargoes/sanctions-programs-and-country-information'
  },
  {
    id: 'un_sanctions',
    name: 'UN Sanctions',
    type: 'government',
    category: 'sanctions',
    icon: '🌍',
    description: 'United Nations consolidated sanctions list',
    capabilities: ['Entity screening', 'Committee information', 'Narrative summaries'],
    status: 'active',
    region: 'Global',
    updateFrequency: 'Weekly',
    documentation: 'https://www.un.org/sc/suborg/en/'
  },
  {
    id: 'eu_sanctions',
    name: 'EU Consolidated Sanctions List',
    type: 'government',
    category: 'sanctions',
    icon: '🇪🇺',
    description: 'European Union consolidated sanctions list',
    capabilities: ['Entity screening', 'Terrorist financing', 'Weapons sanctions'],
    status: 'active',
    region: 'EU',
    updateFrequency: 'Daily',
    documentation: 'https://ec.europa.eu/competition/sanctions/'
  },
  {
    id: 'pep_scan',
    name: 'PEP & Family Scan',
    type: 'government',
    category: 'politically_exposed',
    icon: '👤',
    description: 'Politically Exposed Persons and family member screening',
    capabilities: ['PEP identification', 'Family relationship mapping', 'Historical PEP status'],
    status: 'active',
    region: 'Global',
    updateFrequency: 'Monthly',
    documentation: 'https://www.fatf-gafi.org/recommendations/recommendation-12/'
  },
  {
    id: 'adverse_media',
    name: 'Adverse Media Monitoring',
    type: 'news',
    category: 'reputation',
    icon: '📰',
    description: 'Negative press and media reports on entities',
    capabilities: ['News monitoring', 'Press coverage', 'Reputation risk scoring'],
    status: 'active',
    region: 'Global',
    updateFrequency: 'Real-time',
    documentation: 'https://www.imf.org/external/pubs/ft/fad/2020/pdf/english/chap3.pdf'
  },
  {
    id: 'blockscout',
    name: 'BlockScout',
    type: 'blockchain',
    category: 'monitoring',
    icon: '⛓️',
    description: 'Blockchain address risk assessment and transaction monitoring',
    capabilities: ['Address screening', 'Transaction analysis', 'Risk scoring'],
    status: 'active',
    region: 'Multi-chain (Ethereum, Polygon, etc.)',
    updateFrequency: 'Real-time',
    documentation: 'https://blockscout.com/'
  },
  {
    id: 'chainalysis',
    name: 'Chainalysis Risk Scoring',
    type: 'blockchain',
    category: 'monitoring',
    icon: '🔗',
    description: 'Blockchain illicit activity detection and entity clustering',
    capabilities: ['Illicit detection', 'Entity clustering', 'AML/CFT scoring'],
    status: 'requires_api_key',
    region: 'Multi-chain',
    updateFrequency: 'Real-time',
    documentation: 'https://www.chainalysis.com/'
  },
  {
    id: 'defillama',
    name: 'DefiLlama Protocol Intelligence',
    type: 'blockchain',
    category: 'enrichment',
    icon: '📊',
    description: 'DeFi protocol data and risk metrics',
    capabilities: ['Protocol metrics', 'TVL tracking', 'Yield analysis'],
    status: 'active',
    region: 'Multi-chain DeFi',
    updateFrequency: 'Hourly',
    documentation: 'https://defillama.com/'
  },
  {
    id: 'ankr',
    name: 'Ankr Web3 Infrastructure',
    type: 'blockchain',
    category: 'enrichment',
    icon: '🌐',
    description: 'Multi-chain RPC and data services for blockchain enrichment',
    capabilities: ['Token data', 'NFT metadata', 'Address enrichment'],
    status: 'active',
    region: 'Multi-chain',
    updateFrequency: 'Real-time',
    documentation: 'https://www.ankr.com/'
  },
  {
    id: 'serpro',
    name: 'Serpro APIs (Brazil)',
    type: 'government',
    category: 'compliance',
    icon: '🇧🇷',
    description: 'Brazilian government APIs for identity and corporate data',
    capabilities: ['CPF validation', 'CNPJ verification', 'Birth certificate lookup'],
    status: 'active',
    region: 'Brazil',
    updateFrequency: 'Real-time',
    documentation: 'https://www.gov.br/cidadania/pt-br/acesso-a-informacao/perguntas-frequentes/serpro'
  }
];

export default function SourceManagement({ company }) {
  const C = useTheme();
  const { showToast } = useToast();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('active'); // active, marketplace, keywords
  const [keywords, setKeywords] = useState([]);
  const [showKeywordForm, setShowKeywordForm] = useState(false);
  const [newKeyword, setNewKeyword] = useState({
    keyword: '',
    category: 'risk_flag',
    risk_level: 'high',
    is_regex: false,
  });

  useEffect(() => {
    if (company?.id) {
      loadSources();
      loadKeywords();
    }
  }, [company?.id]);

  async function loadSources() {
    if (!company?.id) return;
    try {
      setLoading(true);
      const { data, error } = await db
        .from('data_sources')
        .select('*')
        .eq('company_id', company.id)
        .order('name', { ascending: true });

      if (error) throw error;
      setSources(data || []);
    } catch (err) {
      showToast('Error loading sources', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function loadKeywords() {
    if (!company?.id) return;
    try {
      const { data, error } = await db
        .from('custom_keywords')
        .select('*')
        .eq('company_id', company.id)
        .eq('is_active', true)
        .order('keyword', { ascending: true });

      if (error) throw error;
      setKeywords(data || []);
    } catch (err) {
      console.error('Error loading keywords:', err);
    }
  }

  async function handleAddSource(sourceName) {
    try {
      const source = BUILTIN_SOURCES.find(s => s.name === sourceName);
      if (!source) {
        showToast('Source not found', 'error');
        return;
      }

      const { error } = await db
        .from('data_sources')
        .insert([{
          company_id: company.id,
          name: source.name,
          source_type: source.type,
          category: source.category,
          is_enabled: true,
          is_custom: false,
        }]);

      if (error) throw error;
      showToast(`Added ${source.name}`, 'success');
      loadSources();
    } catch (err) {
      showToast('Error adding source', 'error');
      console.error(err);
    }
  }

  async function handleToggleSource(sourceId, isEnabled) {
    try {
      const { error } = await db
        .from('data_sources')
        .update({ is_enabled: !isEnabled })
        .eq('id', sourceId);

      if (error) throw error;
      showToast(isEnabled ? 'Source disabled' : 'Source enabled', 'success');
      loadSources();
    } catch (err) {
      showToast('Error updating source', 'error');
      console.error(err);
    }
  }

  async function handleAddKeyword() {
    if (!newKeyword.keyword.trim()) {
      showToast('Keyword cannot be empty', 'error');
      return;
    }

    try {
      const { error } = await db
        .from('custom_keywords')
        .insert([{
          company_id: company.id,
          keyword: newKeyword.keyword,
          category: newKeyword.category,
          risk_level: newKeyword.risk_level,
          is_regex: newKeyword.is_regex,
          is_active: true,
        }]);

      if (error) throw error;
      showToast('Keyword added', 'success');
      setNewKeyword({ keyword: '', category: 'risk_flag', risk_level: 'high', is_regex: false });
      setShowKeywordForm(false);
      loadKeywords();
    } catch (err) {
      showToast('Error adding keyword', 'error');
      console.error(err);
    }
  }

  async function handleDeleteKeyword(id) {
    if (!confirm('Delete this keyword?')) return;
    try {
      const { error } = await db
        .from('custom_keywords')
        .delete()
        .eq('id', id);

      if (error) throw error;
      showToast('Keyword deleted', 'success');
      loadKeywords();
    } catch (err) {
      showToast('Error deleting keyword', 'error');
      console.error(err);
    }
  }

  const addedSources = sources.map(s => s.name);
  const availableSources = BUILTIN_SOURCES.filter(s => !addedSources.includes(s.name));

  if (loading) {
    return <div style={{ padding: '2rem', color: C.textDim }}>Loading source management...</div>;
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: C.text, marginBottom: '0.5rem', fontFamily: "'Syne', sans-serif" }}>
          Source Management
        </h2>
        <p style={{ color: C.textDim, fontSize: '0.9rem' }}>
          Manage data sources, keywords, and integrations for compliance screening.
        </p>
      </div>

      {/* View tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        {[
          { value: 'active', label: `📡 Active Sources (${sources.filter(s => s.is_enabled).length})` },
          { value: 'marketplace', label: `🛒 Add More (${availableSources.length})` },
          { value: 'keywords', label: `📝 Watchwords (${keywords.length})` },
        ].map(v => (
          <button
            key={v.value}
            onClick={() => setView(v.value)}
            style={{
              padding: '0.6rem 1rem',
              background: view === v.value ? C.accent : 'transparent',
              color: view === v.value ? '#0A0E1A' : C.text,
              border: `1px solid ${view === v.value ? C.accent : C.border}`,
              borderRadius: 6,
              cursor: 'pointer',
              fontFamily: "'DM Mono', monospace",
              fontWeight: view === v.value ? 700 : 400,
              fontSize: '0.85rem'
            }}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Active Sources */}
      {view === 'active' && (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {sources.filter(s => s.is_enabled).length === 0 ? (
            <div style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: '2rem',
              textAlign: 'center',
              color: C.textDim
            }}>
              No active sources. Visit the Marketplace to add sources.
            </div>
          ) : (
            sources.filter(s => s.is_enabled).map(source => {
              const builtinData = BUILTIN_SOURCES.find(b => b.name === source.name);
              return (
                <div
                  key={source.id}
                  style={{
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: '1rem',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>
                        {builtinData?.icon} {source.name}
                        {source.is_custom && <span style={{ color: C.accent, marginLeft: '0.5rem', fontSize: '0.8rem' }}>Custom</span>}
                      </div>
                      {builtinData?.description && (
                        <div style={{ color: C.textDim, fontSize: '0.8rem', marginTop: '0.3rem' }}>
                          {builtinData.description}
                        </div>
                      )}
                      <div style={{ color: C.textMid, fontSize: '0.75rem', marginTop: '0.4rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        {builtinData?.region && <span>📍 {builtinData.region}</span>}
                        {builtinData?.updateFrequency && <span>🔄 {builtinData.updateFrequency}</span>}
                        {builtinData?.status && (
                          <span style={{ color: builtinData.status === 'active' ? '#51CF66' : '#FFA94D' }}>
                            ● {builtinData.status.replace('_', ' ').toUpperCase()}
                          </span>
                        )}
                      </div>
                      {builtinData?.capabilities && (
                        <div style={{ color: C.textDim, fontSize: '0.75rem', marginTop: '0.5rem' }}>
                          <strong>Capabilities:</strong> {builtinData.capabilities.join(', ')}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                      {builtinData?.documentation && (
                        <a
                          href={builtinData.documentation}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: '0.4rem 0.8rem',
                            background: 'transparent',
                            border: `1px solid ${C.accent}44`,
                            color: C.accent,
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            fontFamily: "'DM Mono', monospace",
                            textDecoration: 'none',
                            textAlign: 'center'
                          }}
                        >
                          Docs
                        </a>
                      )}
                      <button
                        onClick={() => handleToggleSource(source.id, true)}
                        style={{
                          padding: '0.4rem 0.8rem',
                          background: 'transparent',
                          border: `1px solid #FF6B6B66`,
                          color: '#FF6B6B',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          fontFamily: "'DM Mono', monospace",
                          fontWeight: 600
                        }}
                      >
                        Disable
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Marketplace */}
      {view === 'marketplace' && (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {availableSources.length === 0 ? (
            <div style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: '2rem',
              textAlign: 'center',
              color: C.textDim
            }}>
              All available sources are already enabled!
            </div>
          ) : (
            availableSources.map(source => (
              <div
                key={source.name}
                style={{
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  padding: '1rem',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>
                      {source.icon} {source.name}
                    </div>
                    {source.description && (
                      <div style={{ color: C.textDim, fontSize: '0.8rem', marginTop: '0.3rem' }}>
                        {source.description}
                      </div>
                    )}
                    <div style={{ color: C.textMid, fontSize: '0.75rem', marginTop: '0.4rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      {source.region && <span>📍 {source.region}</span>}
                      {source.updateFrequency && <span>🔄 {source.updateFrequency}</span>}
                      {source.status && (
                        <span style={{ color: source.status === 'active' ? '#51CF66' : '#FFA94D' }}>
                          ● {source.status.replace('_', ' ').toUpperCase()}
                        </span>
                      )}
                    </div>
                    {source.capabilities && (
                      <div style={{ color: C.textDim, fontSize: '0.75rem', marginTop: '0.5rem' }}>
                        <strong>Capabilities:</strong> {source.capabilities.join(', ')}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column', marginLeft: '1rem' }}>
                    {source.documentation && (
                      <a
                        href={source.documentation}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '0.4rem 0.8rem',
                          background: 'transparent',
                          border: `1px solid ${C.accent}44`,
                          color: C.accent,
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: '0.7rem',
                          fontFamily: "'DM Mono', monospace",
                          textDecoration: 'none',
                          textAlign: 'center',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Docs
                      </a>
                    )}
                    <button
                      onClick={() => handleAddSource(source.name)}
                      style={{
                        padding: '0.4rem 0.8rem',
                        background: `${C.accent}22`,
                        color: C.accent,
                        border: `1px solid ${C.accent}`,
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontFamily: "'DM Mono', monospace",
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Add Source
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Keywords */}
      {view === 'keywords' && (
        <div>
          {!showKeywordForm && (
            <button
              onClick={() => setShowKeywordForm(true)}
              style={{
                padding: '0.6rem 1.2rem',
                background: `${C.accent}22`,
                color: C.accent,
                border: `1px solid ${C.accent}`,
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: "'DM Mono', monospace",
                fontWeight: 700,
                fontSize: '0.85rem',
                marginBottom: '2rem'
              }}
            >
              + Add Keyword
            </button>
          )}

          {showKeywordForm && (
            <div style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <h3 style={{ color: C.text, marginBottom: '1rem', fontFamily: "'Syne', sans-serif" }}>
                Add Red-Flag Keyword
              </h3>

              <div style={{ display: 'grid', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                    Keyword / Pattern *
                  </label>
                  <input
                    type="text"
                    value={newKeyword.keyword}
                    onChange={(e) => setNewKeyword({...newKeyword, keyword: e.target.value})}
                    placeholder="e.g., 'money laundering' or regex pattern"
                    style={{
                      width: '100%',
                      padding: '0.6rem',
                      background: C.bg,
                      border: `1px solid ${C.border}`,
                      borderRadius: 6,
                      color: C.text,
                      fontFamily: "'DM Mono', monospace",
                      fontSize: '0.9rem'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                      Category
                    </label>
                    <select
                      value={newKeyword.category}
                      onChange={(e) => setNewKeyword({...newKeyword, category: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: C.bg,
                        border: `1px solid ${C.border}`,
                        borderRadius: 6,
                        color: C.text,
                        fontFamily: "'DM Mono', monospace",
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="risk_flag">Risk Flag</option>
                      <option value="positive">Positive Signal</option>
                      <option value="neutral">Neutral</option>
                      <option value="escalation">Escalation Trigger</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                      Risk Level
                    </label>
                    <select
                      value={newKeyword.risk_level}
                      onChange={(e) => setNewKeyword({...newKeyword, risk_level: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: C.bg,
                        border: `1px solid ${C.border}`,
                        borderRadius: 6,
                        color: C.text,
                        fontFamily: "'DM Mono', monospace",
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>

                <label style={{ color: C.text, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newKeyword.is_regex}
                    onChange={(e) => setNewKeyword({...newKeyword, is_regex: e.target.checked})}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.9rem' }}>Is Regex Pattern</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={handleAddKeyword}
                  style={{
                    padding: '0.6rem 1.2rem',
                    background: C.accent,
                    color: '#0A0E1A',
                    border: 'none',
                    borderRadius: 6,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: "'DM Mono', monospace",
                    fontSize: '0.85rem'
                  }}
                >
                  Add Keyword
                </button>
                <button
                  onClick={() => setShowKeywordForm(false)}
                  style={{
                    padding: '0.6rem 1.2rem',
                    background: 'transparent',
                    border: `1px solid ${C.border}`,
                    color: C.textMid,
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontFamily: "'DM Mono', monospace",
                    fontSize: '0.85rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Keywords List */}
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {keywords.length === 0 ? (
              <div style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: '2rem',
                textAlign: 'center',
                color: C.textDim
              }}>
                No keywords configured yet.
              </div>
            ) : (
              keywords.map(kw => (
                <div
                  key={kw.id}
                  style={{
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: '0.75rem 1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '0.85rem'
                  }}
                >
                  <div>
                    <div style={{ color: C.text, fontWeight: 600 }}>
                      {kw.keyword}
                      {kw.is_regex && <span style={{ color: C.textDim, marginLeft: '0.5rem' }}>(regex)</span>}
                    </div>
                    <div style={{ color: C.textDim, fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      {kw.category} • Risk: {kw.risk_level}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteKeyword(kw.id)}
                    style={{
                      padding: '0.3rem 0.6rem',
                      background: 'transparent',
                      border: `1px solid #FF6B6B66`,
                      color: '#FF6B6B',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.7rem',
                      fontFamily: "'DM Mono', monospace"
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
